/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect } from 'react';
import { useTheme } from 'styled-components';
import { Icon, tokens, orange, inkA, useIsMobile } from 'components/v3';

// Series detail modal — opens from the row's View action. Mirrors the Tracks
// TrackDetailModal, adapted for series data (website + promoter contact instead
// of timezone / size / address).
const SeriesDetailModal = ({ series, statusNode, onEdit, onClose }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!series) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [series, onClose]);

  if (!series) return null;

  const typeLabel =
    (series.type && (series.type.key || series.type.value)) || '—';
  const events = series.eventsCount || 0;
  const website = series.website || '';
  const promoter = series.promoter_name || '';
  const contact = series.promoter_email || series.promoter_phone || '';

  const Meta = ({ label, value }) => (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          fontSize: '10.5px',
          fontWeight: 800,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: t.label,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
        {value}
      </span>
    </span>
  );

  const DRow = ({ label, value, icon }) => (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}
    >
      <span
        style={{
          fontSize: '10.5px',
          fontWeight: 800,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: t.label,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 15,
          fontWeight: 700,
          color: value ? t.text : t.faint,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {icon && <Icon name={icon} size={15} color={t.primary} />}
        {value || '—'}
      </span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.66)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 12 : 28,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 720,
          maxHeight: 'calc(100vh - 56px)',
          overflowY: 'auto',
          background: t.card,
          border: '1px solid ' + t.border,
          borderRadius: 18,
          boxShadow: '0 30px 90px rgba(0,0,0,0.6)',
          padding: isMobile ? '20px 18px' : '26px 28px',
        }}
      >
        {/* Header */}
        {(() => {
          const editBtn = (
            <button
              type="button"
              onClick={onEdit}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                height: 36,
                padding: '0 15px',
                borderRadius: 9,
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid ' + orange(0.55),
                color: t.primary,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <Icon name="square-pen" size={14} color={t.primary} /> Edit
            </button>
          );
          const closeBtn = (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 9,
                cursor: 'pointer',
                background: inkA(theme, 0.05),
                border: '1px solid ' + t.border,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <Icon name="x" size={17} color={inkA(theme, 0.7)} />
            </button>
          );
          const statusRow = (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: t.sub }}>
                Status
              </span>
              {statusNode}
            </div>
          );

          return (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? 12 : 18,
                }}
              >
                <div
                  style={{
                    width: isMobile ? 72 : 96,
                    height: isMobile ? 46 : 60,
                    flexShrink: 0,
                    borderRadius: 10,
                    background: t.inset,
                    border: '1px solid ' + t.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 6,
                  }}
                >
                  {series.logo ? (
                    <img
                      src={series.logo}
                      alt={series.short_name || ''}
                      style={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: isMobile ? 34 : 48,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        color: inkA(theme, 0.66),
                        textTransform: 'uppercase',
                        lineHeight: 1.05,
                      }}
                    >
                      {series.short_name}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h1
                    style={{
                      fontSize: isMobile ? 20 : 24,
                      fontWeight: 800,
                      color: t.text,
                      margin: 0,
                      letterSpacing: '-0.4px',
                    }}
                  >
                    {series.name}
                  </h1>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: isMobile ? '6px 16px' : 16,
                      marginTop: 9,
                    }}
                  >
                    <Meta label="Type" value={typeLabel} />
                    <Meta
                      label="Events"
                      value={`${events} ${events === 1 ? 'event' : 'events'}`}
                    />
                  </div>
                </div>
                {isMobile ? (
                  closeBtn
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 12,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      {editBtn}
                      {closeBtn}
                    </div>
                    {statusRow}
                  </div>
                )}
              </div>
              {isMobile && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  {statusRow}
                  {editBtn}
                </div>
              )}
            </>
          );
        })()}

        {/* Quick chips */}
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18 }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 15px',
              borderRadius: 11,
              background: t.inset,
              border: '1px solid ' + t.border,
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                flexShrink: 0,
                background: orange(0.13),
                border: '1px solid ' + orange(0.55),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="calendar" size={15} color={t.primary} />
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>
              {events} {events === 1 ? 'event' : 'events'}
            </span>
          </div>
          {website ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 15px',
                borderRadius: 11,
                background: t.inset,
                border: '1px solid ' + t.border,
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: orange(0.13),
                  border: '1px solid ' + orange(0.55),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="globe" size={15} color={t.primary} />
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>
                {website}
              </span>
            </div>
          ) : null}
        </div>

        {/* Details */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.7px',
            textTransform: 'uppercase',
            color: t.primary,
            margin: '22px 0 12px',
          }}
        >
          Details
        </div>
        <div
          style={{
            background: t.inset,
            border: '1px solid ' + t.border,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 18,
              padding: '16px 18px',
              borderBottom: '1px solid ' + inkA(theme, 0.07),
            }}
          >
            <DRow label="Series Type" value={typeLabel} />
            <DRow label="Events" value={String(events)} />
          </div>
          <div
            style={{
              padding: '16px 18px',
              borderBottom: '1px solid ' + inkA(theme, 0.07),
            }}
          >
            <DRow label="Website" value={website} icon="globe" />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 18,
              padding: '16px 18px',
            }}
          >
            <DRow label="Promoter" value={promoter} icon="user" />
            <DRow label="Contact" value={contact} icon="mail" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesDetailModal;
